import React from 'react'
import PropTypes from 'prop-types'
import {
  makeStyles,
  CircularProgress
} from '@material-ui/core'
import HistoryIcon from '@material-ui/icons/History'
import InvestigationTimeline from './InvestigationTimeline'
import ReportView from './ReportView'
import QuestionsCard from './QuestionsCard'
import StuckView from './StuckView'

// The research-agent UI used to live inside a floating MUI <Popper>
// anchored to a toolbar button. It is now hosted by the generic
// `utilityPanel/UtilityPanel`, which owns header chrome, layout
// presets, drag-to-resize, and the close button. The pieces below are
// the parts that didn't depend on the popper at all -- the view-kind
// switch (`InvestigationBody`), the footer status line (`TrayFooter`),
// and the report helper (`stepsFromReport`) -- exported by name so
// `utilityPanel/tools/ResearchTool` can compose them.

const useStyles = makeStyles(() => ({
  centeredMessage: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: '0 24px',
    minHeight: 220,
    textAlign: 'center',
    fontSize: 13,
    color: 'var(--color-neutral-500, #6b7280)'
  },
  centeredMessageHeading: {
    fontSize: 13,
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-ai-700)'
  },
  errorMessage: {
    color: 'var(--color-danger-600, #c62828)',
    fontSize: 13
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 14px',
    minHeight: 32,
    fontSize: 11,
    color: 'var(--color-neutral-500, #6b7280)'
  },
  footerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12
  }
}))

export function InvestigationBody ({ view, sessionId, isActing, onAnswerQuestions, onContinue }) {
  const classes = useStyles()

  if (sessionId == null && view.kind === 'idle') {
    return (
      <div className={classes.centeredMessage}>
        <HistoryIcon fontSize='small' />
        <span className={classes.centeredMessageHeading}>No investigation selected</span>
        <span>Right-click a row in the grid to start a new investigation, or pick one from Recent investigations.</span>
      </div>
    )
  }

  switch (view.kind) {
    case 'idle':
      return (
        <div className={classes.centeredMessage}>
          <CircularProgress size={20} style={{ color: 'var(--color-ai-500)' }} />
          <span>Loading investigation&hellip;</span>
        </div>
      )
    case 'starting':
      return (
        <div className={classes.centeredMessage}>
          <CircularProgress size={20} style={{ color: 'var(--color-ai-500)' }} />
          <span>Starting investigation&hellip;</span>
        </div>
      )
    case 'start_failed':
      return (
        <div className={classes.centeredMessage}>
          <span className={classes.errorMessage}>Could not start the investigation.</span>
          <span>{view.error?.message || 'Unknown error'}</span>
        </div>
      )
    case 'investigating': {
      const steps = stepsFromReport(view.report)
      return <InvestigationTimeline steps={steps} cycle />
    }
    case 'needs_input': {
      const steps = stepsFromReport(view.report)
      const questions = view.report?.latestQuestions || view.report?.report?.questions || []
      return (
        <>
          <QuestionsCard
            questions={questions}
            isSubmitting={isActing}
            onSubmit={(answers) => onAnswerQuestions(answers)}
          />
          <InvestigationTimeline steps={steps} busyLabel='Waiting for your input&hellip;' />
        </>
      )
    }
    case 'complete':
      return <ReportView report={view.report} />
    case 'stuck':
      return (
        <>
          <StuckView
            status={view.report?.status}
            threadStatus={view.report?.threadStatus}
            onContinue={onContinue}
            isContinuing={isActing}
          />
          <ReportView report={view.report} />
        </>
      )
    case 'poll_failed':
      return (
        <div className={classes.centeredMessage}>
          <span className={classes.errorMessage}>Could not load the latest report state.</span>
          <span>{view.error?.message || 'Unknown error'}</span>
        </div>
      )
    default:
      return null
  }
}

export function TrayFooter ({ mode, view, sessionId }) {
  const classes = useStyles()
  if (mode === 'history') {
    return <div className={classes.footer}><span>&nbsp;</span></div>
  }
  const report = view.report
  const evidenceCount = report?.evidenceCount
  const stepCount = stepsFromReport(report).length
  return (
    <div className={classes.footer}>
      <div className={classes.footerLeft}>
        {sessionId != null && <span>Session #{sessionId}</span>}
        {stepCount > 0 && <span>{stepCount} step{stepCount === 1 ? '' : 's'}</span>}
        {typeof evidenceCount === 'number' && evidenceCount > 0 && (
          <span>{evidenceCount} audit event{evidenceCount === 1 ? '' : 's'}</span>
        )}
      </div>
    </div>
  )
}

export function stepsFromReport (report) {
  const inner = report?.report
  return Array.isArray(inner?.steps) ? inner.steps : []
}

const viewShape = PropTypes.shape({
  kind: PropTypes.string.isRequired,
  report: PropTypes.object,
  error: PropTypes.shape({ message: PropTypes.string })
})

InvestigationBody.propTypes = {
  view: viewShape.isRequired,
  sessionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isActing: PropTypes.bool,
  onAnswerQuestions: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired
}

TrayFooter.propTypes = {
  mode: PropTypes.oneOf(['history', 'investigation']).isRequired,
  view: viewShape.isRequired,
  sessionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
}
